import type { ReferentialAxis } from '@/core/domain/referential/axis/ReferentialAxis.ts'
import type { LearningCourse } from '@/core/domain/learning/course/LearningCourse.ts'
import type { ReferentialTask } from '@/core/domain/referential/task/ReferentialTask.ts'
import type { Quiz } from '@/core/domain/quiz/Quiz.ts'
import type { LearningModule } from '@/core/domain/learning/module/LearningModule.ts'
import type { LearningGrain } from '@/core/domain/learning/grain/LearningGrain.ts'
import { QuizType } from '@/core/domain/resources/enums/QuizType.ts'
import type { LearningSupport } from '@/core/domain/learning/support/LearningSupport.ts'
import type { QuizQuestion } from '@/core/domain/quiz/QuizQuestion.ts'

/**
 * Implementation of an axis in the Naboo content
 * @param axis - The axis to represent
 * @param learningCourses - The learning courses to represent
 */
export class NabooContentAxis {
  id: number = 0
  title: string = ''
  order: number = 0
  learningCourses: NabooContentLearningCourse[] = []

  constructor(axis: ReferentialAxis, learningCourses: LearningCourse[]) {
    this.id = axis.id
    this.title = axis.title
    this.order = axis.order
    this.learningCourses =
      learningCourses.map((learningCourse) => new NabooContentLearningCourse(learningCourse)) ?? []
  }
}

/**
 * Implementation of a Learning Course in the Naboo content
 * @implements INabooContentLearningCourseDto
 * @param learningCourse - The learning course to represent
 */
export class NabooContentLearningCourse {
  id: number = 0
  title: string = ''
  order: number = 0
  name: string = ''
  diagnosticStart: NabooContentDiagnostic
  diagnosticEnd: NabooContentDiagnostic
  learningModules: NabooContentLearningModule[] = []

  constructor(learningCourse: LearningCourse) {
    this.id = learningCourse.id
    this.title = learningCourse.title
    this.order = learningCourse.order
    this.name = learningCourse.name
    this.diagnosticStart = new NabooContentDiagnostic(learningCourse, QuizType.Values.DIAG_START)
    this.diagnosticEnd = new NabooContentDiagnostic(learningCourse, QuizType.Values.DIAG_END)
    this.learningModules =
      learningCourse.learningModules.map(
        (learningModule) => new NabooContentLearningModule(learningModule)
      ) ?? []
  }

  get children() {
    return [this.diagnosticStart, ...this.learningModules.sort((a, b) => a.order - b.order), this.diagnosticEnd]
  }
}

/**
 * Implementation of a Learning Module in the Naboo content
 * @param learningModule - The learning module to represent
 */
export class NabooContentLearningModule {
  id: number = 0
  title: string = ''
  order: number = 0
  name: string
  learningCourseId: number
  learningGrains: NabooContentLearningGrain[] = []

  constructor(learningModule: LearningModule) {
    this.id = learningModule.id
    this.title = learningModule.title
    this.order = learningModule.order
    this.name = learningModule.learningCourseName
    this.learningCourseId = learningModule.learningCourseId
    this.learningGrains =
      learningModule.learningGrains.map(
        (learningGrain) => new NabooContentLearningGrain(learningGrain)
      ).sort((a, b) => a.order - b.order) ?? []
  }

  get countLearningGrains(): number {
    return this.learningGrains.length
  }
}

/**
 * Implementation of a Learning Grain in the Naboo content
 * @implements INabooContentLearningGrainDto
 * @param learningGrain - The learning grain to represent
 */
export class NabooContentLearningGrain {
  id: number = 0
  title: string = ''
  order: number = 0
  quizPre: NabooContentLearningGrainQuiz | null = null
  quizPost: NabooContentLearningGrainQuiz | null = null
  learningSupports: NabooContentLearningSupport[] = []
  postQuizVersionNumber: number | null = null

  constructor(learningGrain: LearningGrain) {
    this.id = learningGrain.id
    this.title = learningGrain.title
    this.order = learningGrain.order
    this.postQuizVersionNumber = learningGrain.postQuizVersionNumber ?? null
    if (learningGrain.quizPre)
      this.quizPre = new NabooContentLearningGrainQuiz(
        learningGrain.quizPre,
        this.calculateQuizPreOrder()
      )
    if (learningGrain.quizPost)
      this.quizPost = new NabooContentLearningGrainQuiz(
        learningGrain.quizPost,
        this.calculateQuizPostOrder()
      )
    this.learningSupports =
      learningGrain.learningSupports.map(
        (learningSupport) => new NabooContentLearningSupport(learningSupport)
      ) ?? []
  }

  private calculateQuizPreOrder(): number {
    const items = this.learningSupports.map((support) => support.order).sort((a, b) => a - b)
    return items && items.length > 0 ? items[0] - 1 : 0
  }

  private calculateQuizPostOrder(): number {
    const items = this.learningSupports.map((support) => support.order).sort((a, b) => b - a)
    return items && items.length > 0 ? items[0] + 1 : 99
  }

  get estimatedTime(): number {
    return this.learningSupports
      .map((support) => support.estimatedTime)
      .reduce((acc, estimatedTime) => acc + estimatedTime, 0)
  }

  get children() {
    // Return the quiz and supports in the order
    return [this.quizPre, ...this.learningSupports, this.quizPost]
      .filter((item) => item !== null)
      .sort(
        (a, b) =>
          (a as NabooContentLearningGrainQuiz | NabooContentLearningSupport).order -
          (b as NabooContentLearningGrainQuiz | NabooContentLearningSupport).order
      )
  }
}

/**
 * Implementation of a Learning Support in the Naboo content
 * @param learningSupport - The learning support to represent
 */
export class NabooContentLearningSupport {
  id: number = 0
  title: string = ''
  order: number = 0
  url: string = ''
  estimatedTime: number = 0

  constructor(learningSupport: LearningSupport) {
    this.id = learningSupport.id
    this.title = `Support ${learningSupport.order}`
    this.order = learningSupport.order
    this.url = learningSupport.url
    this.estimatedTime = learningSupport.estimatedTime
  }
}

/**
 * Implementation of a Diagnostic in the Naboo content
 * @param learningCourse - The learning course to represent
 */
export class NabooContentDiagnostic {
  id: number = 0
  order: number = 0
  name: string
  type: QuizType.Values.DIAG_START | QuizType.Values.DIAG_END
  learningCourseId: number
  learningCourseTitle: string
  tasks: NabooContentDiagnosticTask[] = []

  constructor(
    learningCourse: LearningCourse,
    diagnosticType: QuizType.Values.DIAG_START | QuizType.Values.DIAG_END
  ) {
    this.id = learningCourse.id
    this.order = this.calculateOrder(learningCourse)
    this.name = learningCourse.name
    this.type = diagnosticType
    this.learningCourseId = learningCourse.id
    this.learningCourseTitle = learningCourse.title
    this.tasks =
      learningCourse.domain.tasks
        .filter((t) => {
          if (diagnosticType === QuizType.Values.DIAG_START) {
            return !!t.quizStart && !!t.quizStart.activeVersion
          } else if (diagnosticType === QuizType.Values.DIAG_END) {
            return !!t.quizEnd && !!t.quizEnd.activeVersion
          }
        })
        .map((task) => new NabooContentDiagnosticTask(task, diagnosticType)) ?? []
  }

  get isDiagnosticStart(): boolean {
    return this.type === QuizType.Values.DIAG_START
  }

  get isDiagnosticEnd(): boolean {
    return this.type === QuizType.Values.DIAG_END
  }

  get title(): string {
    if (this.isDiagnosticStart) {
      return "Diagnostic T1 - Test d'entrée"
    }
    else {
      return "Diagnostic T2 - Rapport d'exploration"
    }
  }

  private calculateOrder(learningCourse: LearningCourse): number {
    const items = learningCourse.learningModules.map((module) => module.order).sort((a, b) => a - b)
    return items && items.length > 0 ? items[0] - 1 : 0
  }

  get countQuestions(): number {
    return this.tasks.reduce((acc, task) => acc + task.countQuestions, 0)
  }
}

/**
 * Implementation of a Diagnostic Task in the Naboo content
 * @param task - The task to represent
 */
export class NabooContentDiagnosticTask {
  id: number = 0
  title: string = ''
  order: number = 0
  quiz: Quiz | null = null

  constructor(
    task: ReferentialTask,
    diagnosticType: QuizType.Values.DIAG_START | QuizType.Values.DIAG_END
  ) {
    this.id = task.id
    this.title = task.title
    this.order = task.order
    if (diagnosticType === QuizType.Values.DIAG_START) {
      this.quiz = task.quizStart
    } else if (diagnosticType === QuizType.Values.DIAG_END) {
      this.quiz = task.quizEnd
    }
  }

  get countQuestions(): number {
    return this.getActiveQuestions().length
  }

  get questions(): QuizQuestion[] {
    return this.getActiveQuestions()
  }

  /**
   * Retourne les questions de la version active du quiz
   * @returns Questions de la version active, ou tableau vide si pas de quiz/version active
   */
  private getActiveQuestions(): QuizQuestion[] {
    if (!this.quiz) return []

    const activeVersion = this.quiz.activeVersion
    return activeVersion ? activeVersion.questions : []
  }
}

/**
 * Implementation of a Learning Grain Quiz in the Naboo content
 * @param learningQuiz - The learning quiz to represent
 * @param order - The order of the quiz
 */
export class NabooContentLearningGrainQuiz {
  id: number = 0
  title: string = ''
  order: number = 0
  quiz: Quiz

  constructor(learningQuiz: Quiz, order: number = 0) {
    this.id = learningQuiz.id
    this.title = this.getTitle(learningQuiz)
    this.order = order
    this.quiz = learningQuiz
  }

  private getTitle(learningQuiz: Quiz): string {
    if (learningQuiz.type === QuizType.Values.PRE) {
      return "Quiz d'introduction"
    } else if (learningQuiz.type === QuizType.Values.POST) {
      return 'Quiz de contrôle des connaissances'
    } else {
      throw new UnknownQuizTypeNabooContent(learningQuiz.type)
    }
  }

  get countQuestions(): number {
    const activeVersion = this.quiz.activeVersion
    return activeVersion ? activeVersion.questions.length : 0
  }

  /**
   * Retourne les questions de la version active du quiz
   * @returns Questions de la version active, ou tableau vide si pas de version active
   */
  get questions(): QuizQuestion[] {
    const activeVersion = this.quiz.activeVersion
    return activeVersion ? activeVersion.questions : []
  }
}

// Exceptions
export class UnknownQuizTypeNabooContent extends Error {
  constructor(type: QuizType.Values) {
    super(`Le type de Quiz ${type} est incompatible`)
  }
}
